type CronField = {
  values: Set<number>;
  sortedValues: number[];
  isWildcard: boolean;
};

type TimeZoneParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
};

export type CronPreview = {
  dates: Date[];
  error?: string;
};

const MONTH_NAMES: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

const WEEKDAY_NAMES: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const toInt = (value: string) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

const parseEndpoint = (
  value: string,
  names?: Record<string, number>
): number | null => {
  const normalized = value.trim().toUpperCase();
  if (names?.[normalized] !== undefined) return names[normalized];
  return toInt(normalized);
};

const normalizeValue = (
  value: number,
  min: number,
  max: number,
  isWeekday: boolean
) => {
  if (isWeekday && value === 7) return 0;
  if (value < min || value > max) return null;
  return value;
};

const parseCronField = ({
  field,
  min,
  max,
  label,
  names,
  isWeekday = false,
}: {
  field: string;
  min: number;
  max: number;
  label: string;
  names?: Record<string, number>;
  isWeekday?: boolean;
}): CronField => {
  const values = new Set<number>();
  const tokens = field.split(',').map((token) => token.trim());

  if (!tokens.length || tokens.some((token) => !token)) {
    throw new Error(`${label} has an empty segment`);
  }

  tokens.forEach((token) => {
    const [base, stepRaw, extra] = token.split('/');
    if (extra !== undefined) {
      throw new Error(`${label} has too many step separators`);
    }

    const step = stepRaw === undefined ? 1 : toInt(stepRaw);
    if (!step || step < 1) {
      throw new Error(`${label} step must be a positive number`);
    }

    let start = min;
    let end = max;

    if (base !== '*') {
      if (base.includes('-')) {
        const [startRaw, endRaw, extraRange] = base.split('-');
        if (extraRange !== undefined) {
          throw new Error(`${label} range is invalid`);
        }

        const parsedStart = parseEndpoint(startRaw, names);
        const parsedEnd = parseEndpoint(endRaw, names);
        if (parsedStart === null || parsedEnd === null) {
          throw new Error(`${label} range is invalid`);
        }

        start = parsedStart;
        end = parsedEnd;
      } else {
        const parsedValue = parseEndpoint(base, names);
        if (parsedValue === null) {
          throw new Error(`${label} value is invalid`);
        }

        start = parsedValue;
        end = stepRaw === undefined ? parsedValue : max;
      }
    }

    if (start > end) {
      throw new Error(`${label} range must start before it ends`);
    }

    for (let value = start; value <= end; value += step) {
      const normalized = normalizeValue(value, min, max, isWeekday);
      if (normalized === null) {
        throw new Error(`${label} value is out of range`);
      }
      values.add(normalized);
    }
  });

  const sortedValues = Array.from(values).sort((a, b) => a - b);
  const fullRangeCount = isWeekday ? 7 : max - min + 1;

  return {
    values,
    sortedValues,
    isWildcard: sortedValues.length === fullRangeCount,
  };
};

const parseCronExpression = (expression: string) => {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) {
    throw new Error('Use a 5-part cron expression');
  }

  return {
    minute: parseCronField({
      field: parts[0],
      min: 0,
      max: 59,
      label: 'Minute',
    }),
    hour: parseCronField({
      field: parts[1],
      min: 0,
      max: 23,
      label: 'Hour',
    }),
    dayOfMonth: parseCronField({
      field: parts[2],
      min: 1,
      max: 31,
      label: 'Day of month',
    }),
    month: parseCronField({
      field: parts[3],
      min: 1,
      max: 12,
      label: 'Month',
      names: MONTH_NAMES,
    }),
    dayOfWeek: parseCronField({
      field: parts[4],
      min: 0,
      max: 7,
      label: 'Day of week',
      names: WEEKDAY_NAMES,
      isWeekday: true,
    }),
  };
};

const createTimeZoneFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

const getTimeZoneParts = (
  date: Date,
  formatter: Intl.DateTimeFormat
): TimeZoneParts => {
  const parts = formatter.formatToParts(date);
  const valueByType = parts.reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  const year = Number(valueByType.year);
  const month = Number(valueByType.month);
  const day = Number(valueByType.day);
  const hour = Number(valueByType.hour);
  const minute = Number(valueByType.minute);

  return {
    year,
    month,
    day,
    hour,
    minute,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
};

const zonedTimeToUtc = ({
  year,
  month,
  day,
  hour,
  minute,
  formatter,
}: {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  formatter: Intl.DateTimeFormat;
}) => {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const guessParts = getTimeZoneParts(new Date(utcGuess), formatter);
  const guessPartsAsUtc = Date.UTC(
    guessParts.year,
    guessParts.month - 1,
    guessParts.day,
    guessParts.hour,
    guessParts.minute
  );

  return new Date(utcGuess + (utcGuess - guessPartsAsUtc));
};

const doesDayMatch = ({
  day,
  weekday,
  dayOfMonth,
  dayOfWeek,
}: {
  day: number;
  weekday: number;
  dayOfMonth: CronField;
  dayOfWeek: CronField;
}) => {
  const dayOfMonthMatches = dayOfMonth.values.has(day);
  const dayOfWeekMatches = dayOfWeek.values.has(weekday);

  if (dayOfMonth.isWildcard && dayOfWeek.isWildcard) return true;
  if (dayOfMonth.isWildcard) return dayOfWeekMatches;
  if (dayOfWeek.isWildcard) return dayOfMonthMatches;

  return dayOfMonthMatches || dayOfWeekMatches;
};

const isSameZonedMinute = ({
  date,
  formatter,
  year,
  month,
  day,
  hour,
  minute,
}: {
  date: Date;
  formatter: Intl.DateTimeFormat;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}) => {
  const parts = getTimeZoneParts(date, formatter);
  return (
    parts.year === year &&
    parts.month === month &&
    parts.day === day &&
    parts.hour === hour &&
    parts.minute === minute
  );
};

export const buildCronPreview = (
  expression: string,
  timeZone: string,
  count: number = 5
): CronPreview => {
  if (!expression.trim()) return { dates: [] };

  try {
    const parsedCron = parseCronExpression(expression);
    const formatter = createTimeZoneFormatter(timeZone || 'UTC');
    const now = new Date();
    const today = getTimeZoneParts(now, formatter);
    const dates: Date[] = [];

    for (let dayOffset = 0; dayOffset <= 370 && dates.length < count; dayOffset++) {
      const localDate = new Date(
        Date.UTC(today.year, today.month - 1, today.day + dayOffset)
      );
      const year = localDate.getUTCFullYear();
      const month = localDate.getUTCMonth() + 1;
      const day = localDate.getUTCDate();
      const weekday = localDate.getUTCDay();

      if (!parsedCron.month.values.has(month)) continue;
      if (
        !doesDayMatch({
          day,
          weekday,
          dayOfMonth: parsedCron.dayOfMonth,
          dayOfWeek: parsedCron.dayOfWeek,
        })
      ) {
        continue;
      }

      parsedCron.hour.sortedValues.forEach((hour) => {
        parsedCron.minute.sortedValues.forEach((minute) => {
          if (dates.length >= count) return;

          const date = zonedTimeToUtc({
            year,
            month,
            day,
            hour,
            minute,
            formatter,
          });

          if (
            date > now &&
            isSameZonedMinute({
              date,
              formatter,
              year,
              month,
              day,
              hour,
              minute,
            })
          ) {
            dates.push(date);
          }
        });
      });

      dates.sort((a, b) => a.getTime() - b.getTime());
    }

    if (!dates.length) {
      return { dates, error: 'No runs found in the next year' };
    }

    return { dates };
  } catch (error) {
    return {
      dates: [],
      error: error instanceof Error ? error.message : 'Cron expression is invalid',
    };
  }
};

export const formatCronPreviewDate = (date: Date, timeZone: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};
